/**
 * Formularios de **Caja Chica**: saldo inicial, ingreso, egreso, reajuste de
 * saldo y depósito a banco.
 *
 * Son los cinco diálogos de la vista anterior (`caja_chica/index.php`), que aquí
 * comparten el mismo armazón: cada uno valida lo suyo, enseña el «nuevo saldo
 * estimado» mientras se escribe el monto y muestra el error que devuelva la API.
 */

import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { ArchivoInput } from '@/components/ui/archivo-input';
import { Button } from '@/components/ui/button';
import { CampoFecha } from '@/components/ui/calendario';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { cn, formatCurrency } from '@/lib/utils';

import type { CuentaCaja, OpcionCaja } from '../caja-chica.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const ACEPTADOS = 'application/pdf,image/png,image/jpeg';

/** Pie común: cancelar y confirmar. */
function Pie({
  etiqueta,
  guardando,
  puedeGuardar,
  onCerrar,
  onGuardar,
}: {
  etiqueta: string;
  guardando: boolean;
  puedeGuardar: boolean;
  onCerrar: () => void;
  onGuardar: () => void;
}) {
  return (
    <>
      <Button variant="outline" size="sm" onClick={onCerrar} disabled={guardando}>
        Cancelar
      </Button>
      <Button size="sm" loading={guardando} disabled={!puedeGuardar} onClick={onGuardar}>
        {etiqueta}
      </Button>
    </>
  );
}

/** Campo de comprobante (PDF, PNG o JPG). */
function CampoArchivo({
  archivo,
  obligatorio,
  onElegir,
}: {
  archivo: File | null;
  obligatorio: boolean;
  onElegir: (archivo: File | null) => void;
}) {
  return (
    <ArchivoInput
      label={`Comprobante ${obligatorio ? '' : '(opcional)'}`}
      accept={ACEPTADOS}
      nombre={archivo?.name ?? null}
      hint="PDF, PNG o JPG de hasta 5 MB. Se guarda en uploads/caja_chica/."
      onSeleccionar={(elegido) => onElegir(elegido?.archivo ?? null)}
    />
  );
}

/** Etiqueta con el saldo resultante de la operación que se está escribiendo. */
function SaldoEstimado({ saldo, monto, signo }: { saldo: number; monto: string; signo: 1 | -1 }) {
  const cantidad = Number(monto.replace(',', '.')) || 0;
  const nuevo = Math.max(saldo + signo * cantidad, 0);

  return (
    <p className="text-xs text-ink-500">
      Nuevo saldo estimado:{' '}
      <strong className="text-ink-800">{formatCurrency(nuevo)}</strong>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Ingreso y egreso
// ---------------------------------------------------------------------------

export interface DatosMovimientoCaja {
  fecha: string;
  monto: string;
  categoria: string;
  concepto: string;
  referencia: string;
  archivo: File | null;
}

interface DialogoMovimientoCajaProps {
  abierto: boolean;
  /** `ingreso` o `egreso`: cambia el título, las categorías y el signo. */
  tipo: 'ingreso' | 'egreso';
  hoy: string;
  saldo: number;
  categorias: OpcionCaja[];
  guardando: boolean;
  error: string | null;
  onCerrar: () => void;
  onGuardar: (datos: DatosMovimientoCaja) => void;
}

/** Registrar un ingreso o un egreso de caja chica. */
export function DialogoMovimientoCaja({
  abierto,
  tipo,
  hoy,
  saldo,
  categorias,
  guardando,
  error,
  onCerrar,
  onGuardar,
}: DialogoMovimientoCajaProps) {
  const esIngreso = tipo === 'ingreso';
  const [fecha, setFecha] = useState(hoy);
  const [monto, setMonto] = useState('');
  const [categoria, setCategoria] = useState('');
  const [concepto, setConcepto] = useState('');
  const [referencia, setReferencia] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);

  const cantidad = Number(monto.replace(',', '.')) || 0;
  const puedeGuardar =
    Boolean(fecha) &&
    cantidad > 0 &&
    Boolean(categoria) &&
    concepto.trim().length > 0 &&
    referencia.trim().length > 0;

  const guardar = () => {
    if (!puedeGuardar) return;

    onGuardar({
      fecha,
      monto: cantidad.toFixed(2),
      categoria,
      concepto: concepto.trim(),
      referencia: referencia.trim(),
      archivo,
    });
  };

  return (
    <Dialog
      open={abierto}
      onClose={onCerrar}
      title={esIngreso ? 'Registrar ingreso a Caja Chica' : 'Registrar egreso de Caja Chica'}
      description={
        esIngreso
          ? 'El efectivo entra al libro de caja chica y sube el saldo disponible.'
          : 'El efectivo sale del libro de caja chica y baja el saldo disponible.'
      }
      size="lg"
      footer={
        <Pie
          etiqueta={esIngreso ? 'Registrar ingreso' : 'Registrar egreso'}
          guardando={guardando}
          puedeGuardar={puedeGuardar}
          onCerrar={onCerrar}
          onGuardar={guardar}
        />
      }
    >
      <div className="flex flex-col gap-4">
        <Alert variant={esIngreso ? 'success' : 'warning'}>
          Saldo disponible: <strong>{formatCurrency(saldo)}</strong>
        </Alert>

        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="caja-fecha">
              Fecha
            </label>
            <CampoFecha
              id="caja-fecha"
              tipo="date"
              value={fecha}
              onChange={(evento) => setFecha(evento.target.value)}
              aria-label="Fecha del movimiento"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Input
              label="Monto (S/)"
              type="number"
              step="0.01"
              min="0.01"
              value={monto}
              onChange={(evento) => setMonto(evento.target.value)}
            />
            <SaldoEstimado saldo={saldo} monto={monto} signo={esIngreso ? 1 : -1} />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="caja-categoria">
              Categoría
            </label>
            <Select
              id="caja-categoria"
              value={categoria}
              onChange={(evento) => setCategoria(evento.target.value)}
              className={CONTROL}
              aria-label="Categoría"
            >
              <option value="">Seleccionar…</option>
              {categorias.map((opcion: OpcionCaja) => (
                <option key={opcion.valor} value={opcion.valor}>
                  {opcion.etiqueta}
                </option>
              ))}
            </Select>
          </div>

          <Input
            label="Referencia"
            value={referencia}
            placeholder={esIngreso ? 'Nro. documento, voucher…' : 'Factura, recibo…'}
            onChange={(evento) => setReferencia(evento.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700" htmlFor="caja-concepto">
            Concepto / Motivo
          </label>
          <textarea
            id="caja-concepto"
            rows={3}
            maxLength={300}
            className={cn(CONTROL, 'h-auto py-2')}
            value={concepto}
            placeholder={
              esIngreso ? 'Describa el motivo del ingreso' : 'Describa el motivo del egreso'
            }
            onChange={(evento) => setConcepto(evento.target.value)}
          />
        </div>

        <CampoArchivo archivo={archivo} obligatorio={false} onElegir={setArchivo} />
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Saldo inicial
// ---------------------------------------------------------------------------

interface DialogoAperturaProps {
  abierto: boolean;
  hoy: string;
  guardando: boolean;
  error: string | null;
  onCerrar: () => void;
  onGuardar: (datos: { fecha: string; monto: string; concepto: string }) => void;
}

/** Registrar el saldo inicial (sólo se puede una vez, con el libro vacío). */
export function DialogoAperturaCaja({
  abierto,
  hoy,
  guardando,
  error,
  onCerrar,
  onGuardar,
}: DialogoAperturaProps) {
  const [fecha, setFecha] = useState(hoy);
  const [monto, setMonto] = useState('');
  const [concepto, setConcepto] = useState('Saldo inicial de caja chica');

  const cantidad = Number(monto.replace(',', '.')) || 0;
  const puedeGuardar = Boolean(fecha) && cantidad > 0 && concepto.trim().length > 0;

  return (
    <Dialog
      open={abierto}
      onClose={onCerrar}
      title="Registrar saldo inicial"
      description="Este será el primer movimiento del libro de caja chica: sólo se puede registrar una vez."
      footer={
        <Pie
          etiqueta="Registrar saldo inicial"
          guardando={guardando}
          puedeGuardar={puedeGuardar}
          onCerrar={onCerrar}
          onGuardar={() =>
            onGuardar({ fecha, monto: cantidad.toFixed(2), concepto: concepto.trim() })
          }
        />
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="apertura-fecha">
              Fecha
            </label>
            <CampoFecha
              id="apertura-fecha"
              tipo="date"
              value={fecha}
              onChange={(evento) => setFecha(evento.target.value)}
              aria-label="Fecha del saldo inicial"
            />
          </div>

          <Input
            label="Monto inicial (S/)"
            type="number"
            step="0.01"
            min="0.01"
            value={monto}
            onChange={(evento) => setMonto(evento.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700" htmlFor="apertura-concepto">
            Concepto
          </label>
          <textarea
            id="apertura-concepto"
            rows={3}
            maxLength={300}
            className={cn(CONTROL, 'h-auto py-2')}
            value={concepto}
            onChange={(evento) => setConcepto(evento.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Reajuste de saldo
// ---------------------------------------------------------------------------

interface DialogoReajusteProps {
  abierto: boolean;
  hoy: string;
  saldo: number;
  guardando: boolean;
  error: string | null;
  onCerrar: () => void;
  onGuardar: (datos: {
    fecha: string;
    direccion: 'sube' | 'baja';
    monto: string;
    concepto: string;
    referencia: string;
  }) => void;
}

/** Reajustar el saldo (sube o baja) con su motivo obligatorio. */
export function DialogoReajusteCaja({
  abierto,
  hoy,
  saldo,
  guardando,
  error,
  onCerrar,
  onGuardar,
}: DialogoReajusteProps) {
  const [fecha, setFecha] = useState(hoy);
  const [direccion, setDireccion] = useState<'sube' | 'baja'>('sube');
  const [monto, setMonto] = useState('');
  const [concepto, setConcepto] = useState('');
  const [referencia, setReferencia] = useState('');

  const cantidad = Number(monto.replace(',', '.')) || 0;
  const puedeGuardar = Boolean(fecha) && cantidad > 0 && concepto.trim().length > 0;

  return (
    <Dialog
      open={abierto}
      onClose={onCerrar}
      title="Reajustar saldo de caja chica"
      description="El reajuste queda en el libro con su motivo, para que el saldo siga cuadrando."
      footer={
        <Pie
          etiqueta="Aplicar reajuste"
          guardando={guardando}
          puedeGuardar={puedeGuardar}
          onCerrar={onCerrar}
          onGuardar={() =>
            onGuardar({
              fecha,
              direccion,
              monto: cantidad.toFixed(2),
              concepto: concepto.trim(),
              referencia: referencia.trim(),
            })
          }
        />
      }
    >
      <div className="flex flex-col gap-4">
        <Alert variant="info">
          Saldo actual: <strong>{formatCurrency(saldo)}</strong>
        </Alert>

        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="reajuste-fecha">
              Fecha
            </label>
            <CampoFecha
              id="reajuste-fecha"
              tipo="date"
              value={fecha}
              onChange={(evento) => setFecha(evento.target.value)}
              aria-label="Fecha del reajuste"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="reajuste-direccion">
              Dirección
            </label>
            <Select
              id="reajuste-direccion"
              value={direccion}
              onChange={(evento) => setDireccion(evento.target.value as 'sube' | 'baja')}
              className={CONTROL}
              aria-label="Dirección del reajuste"
            >
              <option value="sube">Sube (aumenta el saldo)</option>
              <option value="baja">Baja (disminuye el saldo)</option>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Input
              label="Monto (S/)"
              type="number"
              step="0.01"
              min="0.01"
              value={monto}
              onChange={(evento) => setMonto(evento.target.value)}
            />
            <SaldoEstimado saldo={saldo} monto={monto} signo={direccion === 'baja' ? -1 : 1} />
          </div>

          <Input
            label="Referencia (opcional)"
            value={referencia}
            onChange={(evento) => setReferencia(evento.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700" htmlFor="reajuste-concepto">
            Motivo del reajuste
          </label>
          <textarea
            id="reajuste-concepto"
            rows={3}
            maxLength={300}
            className={cn(CONTROL, 'h-auto py-2')}
            value={concepto}
            placeholder="Indique por qué se reajusta el saldo"
            onChange={(evento) => setConcepto(evento.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Depósito a banco
// ---------------------------------------------------------------------------

interface DialogoDepositoProps {
  abierto: boolean;
  hoy: string;
  saldo: number;
  cuentas: CuentaCaja[];
  bancos: { id: number; descripcion: string }[];
  guardando: boolean;
  error: string | null;
  onCerrar: () => void;
  onGuardar: (datos: {
    fecha: string;
    monto: string;
    cuenta_id: string;
    concepto: string;
    referencia: string;
    archivo: File | null;
  }) => void;
}

/**
 * Depositar el efectivo de caja chica en una cuenta bancaria: descuenta del
 * libro y crea el movimiento `DEP_EFE` de la cuenta elegida.
 */
export function DialogoDepositoCaja({
  abierto,
  hoy,
  saldo,
  cuentas,
  bancos,
  guardando,
  error,
  onCerrar,
  onGuardar,
}: DialogoDepositoProps) {
  const [fecha, setFecha] = useState(hoy);
  const [banco, setBanco] = useState('');
  const [cuenta, setCuenta] = useState('');
  const [monto, setMonto] = useState('');
  const [referencia, setReferencia] = useState('');
  const [concepto, setConcepto] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);

  const cantidad = Number(monto.replace(',', '.')) || 0;
  const cuentasDelBanco = banco ? cuentas.filter((item) => item.banco_id === Number(banco)) : cuentas;
  const puedeGuardar =
    Boolean(fecha) &&
    cantidad > 0 &&
    Boolean(cuenta) &&
    concepto.trim().length > 0 &&
    referencia.trim().length > 0 &&
    archivo !== null;

  return (
    <Dialog
      open={abierto}
      onClose={onCerrar}
      title="Depositar efectivo a banco"
      description="El monto se descuenta de Caja Chica y se acredita como depósito de efectivo en la cuenta elegida."
      size="lg"
      footer={
        <Pie
          etiqueta="Depositar a banco"
          guardando={guardando}
          puedeGuardar={puedeGuardar}
          onCerrar={onCerrar}
          onGuardar={() =>
            onGuardar({
              fecha,
              monto: cantidad.toFixed(2),
              cuenta_id: cuenta,
              concepto: concepto.trim(),
              referencia: referencia.trim(),
              archivo,
            })
          }
        />
      }
    >
      <div className="flex flex-col gap-4">
        <Alert variant="info">
          Saldo disponible en Caja Chica: <strong>{formatCurrency(saldo)}</strong>
        </Alert>

        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="deposito-fecha">
              Fecha del depósito
            </label>
            <CampoFecha
              id="deposito-fecha"
              tipo="date"
              value={fecha}
              onChange={(evento) => setFecha(evento.target.value)}
              aria-label="Fecha del depósito"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Input
              label="Monto a depositar (S/)"
              type="number"
              step="0.01"
              min="0.01"
              value={monto}
              onChange={(evento) => setMonto(evento.target.value)}
            />
            <SaldoEstimado saldo={saldo} monto={monto} signo={-1} />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="deposito-banco">
              Banco
            </label>
            <Select
              id="deposito-banco"
              value={banco}
              onChange={(evento) => {
                setBanco(evento.target.value);
                setCuenta('');
              }}
              className={CONTROL}
              aria-label="Banco destino"
            >
              <option value="">Todos los bancos</option>
              {bancos.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {item.descripcion}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="deposito-cuenta">
              Cuenta destino
            </label>
            <Select
              id="deposito-cuenta"
              value={cuenta}
              onChange={(evento) => setCuenta(evento.target.value)}
              className={CONTROL}
              aria-label="Cuenta destino"
            >
              <option value="">
                {banco ? 'Seleccionar cuenta…' : 'Primero seleccione un banco…'}
              </option>
              {cuentasDelBanco.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {item.descripcion}
                </option>
              ))}
            </Select>
          </div>

          <Input
            label="Nro. de voucher / referencia"
            value={referencia}
            placeholder="Nro. de voucher, operación…"
            onChange={(evento) => setReferencia(evento.target.value)}
          />

          <Input label="Método de depósito" value="Efectivo" readOnly />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700" htmlFor="deposito-concepto">
            Concepto / motivo del depósito
          </label>
          <textarea
            id="deposito-concepto"
            rows={3}
            maxLength={300}
            className={cn(CONTROL, 'h-auto py-2')}
            value={concepto}
            placeholder="Ej: Depósito del efectivo recaudado por caja chica"
            onChange={(evento) => setConcepto(evento.target.value)}
          />
        </div>

        <CampoArchivo archivo={archivo} obligatorio onElegir={setArchivo} />
      </div>
    </Dialog>
  );
}
